// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Post-merge self-check: prove the output is what was promised, or discard it.
 *
 * This runs on the merged file *re-parsed from its own serialized text*, not on
 * the tree that produced it. A merge that looks right in memory and serializes
 * wrongly is exactly the failure this catches, and it is the failure that would
 * otherwise reach a fabricator.
 *
 * The headline assertion is the one the whole interface is built around:
 * **the output differs from Board A in exactly the changes the user selected,
 * and nowhere else.** Everything else here supports it. If any of this fails the
 * output is thrown away rather than offered, because a merge that cannot prove
 * itself is worse than no merge.
 */

import { diffBoards } from '../diff/diff.ts';
import { Board } from '../model/board.ts';
import type { Node } from '../sexp/index.ts';
import { getUuid, walk } from '../sexp/index.ts';
import type { MergePlan } from '../select/plan.ts';

export interface SelfCheckFailure {
  /** The invariant that did not hold, stated as a claim about the output. */
  assertion: string;
  detail: string;
}

export interface SelfCheckResult {
  ok: boolean;
  failures: SelfCheckFailure[];
  /** Object counts in the output, by type. */
  counts: Record<string, number>;
  /**
   * Duplicate UUIDs that were already in the inputs. Reported, never fatal —
   * KiCad itself writes a `dimension` and its nested `gr_text` sharing one.
   */
  preExistingDuplicateUuids: string[];
}

export interface SelfCheckInput {
  a: Board;
  b: Board;
  /** The merged output, re-parsed from its serialized text. */
  merged: Board;
  plan: MergePlan;
  /** Net change in object count per type that the plan accounted for. */
  expectedDelta: Record<string, number>;
}

export function selfCheck(input: SelfCheckInput): SelfCheckResult {
  const { a, b, merged, plan, expectedDelta } = input;
  const failures: SelfCheckFailure[] = [];

  const diff = diffBoards(a, merged);

  // 1. The output differs from Board A only where the user said so.
  //
  //    Measured against the plan, not against the rectangles. A rectangle is the
  //    gesture that gathers changes up; the plan is the statement the user made,
  //    and the two are not the same set. A move is the clearest case: its extent
  //    is the union of where it was and where it went, so a rectangle drawn round
  //    the destination only ever straddles it, and switching a straddling change
  //    on is precisely what that checkbox is for. Demanding containment here
  //    rejected every such merge after building it correctly.
  //
  //    The plan is also the stricter reading. It catches a difference the user
  //    switched *off* inside a rectangle, which containment would have waved
  //    through.
  const strays = diff.substantive.filter((c) => !plan.take.has(c.key));
  if (strays.length > 0) {
    failures.push({
      assertion: 'every difference between Board A and the output is a change you selected',
      detail:
        `${strays.length} difference(s) are not in the plan: ` +
        strays
          .slice(0, 5)
          .map((c) => `${c.kind} ${Board.describe((c.a ?? c.b)!)}`)
          .join('; '),
    });
  }

  // 2. Per-type object counts are Board A's, adjusted by exactly what the plan
  //    said it would add and remove. Anything else means objects appeared or
  //    vanished with no change to account for them.
  const expected = countByType(a);
  for (const [type, delta] of Object.entries(expectedDelta)) {
    expected[type] = (expected[type] ?? 0) + delta;
  }
  const actual = countByType(merged);
  for (const type of new Set([...Object.keys(expected), ...Object.keys(actual)])) {
    const want = expected[type] ?? 0;
    const got = actual[type] ?? 0;
    if (want !== got) {
      failures.push({
        assertion: `object count for "${type}" is Board A's plus what the plan changed`,
        detail: `expected ${want}, found ${got}`,
      });
    }
  }

  // 3. No duplicate UUIDs anywhere in the output — including inside footprints,
  //    where a collision is just as capable of confusing KiCad.
  //
  //    Measured against the *inputs*, not against zero, for the reason above.
  //    What must never happen is the merge creating a new one.
  const preExisting = new Set([...findDuplicateUuids(a.root), ...findDuplicateUuids(b.root)]);
  const duplicates = findDuplicateUuids(merged.root);
  const introduced = duplicates.filter((u) => !preExisting.has(u));
  if (introduced.length > 0) {
    failures.push({
      assertion: 'the merge introduces no duplicate UUIDs',
      detail: `${introduced.length} new duplicate(s): ${introduced.slice(0, 5).join(', ')}`,
    });
  }
  const carriedOver = duplicates.filter((u) => preExisting.has(u));

  // 4. No schematic path appears twice. A duplicated `path` means two footprints
  //    claim to be the same component, which desynchronises the board from the
  //    schematic in a way KiCad does not warn about.
  const outputPaths = new Map<string, number>();
  for (const o of merged.objectsOfType('footprint')) {
    if (o.path === undefined || o.path === '') continue;
    outputPaths.set(o.path, (outputPaths.get(o.path) ?? 0) + 1);
  }
  for (const [p, n] of outputPaths) {
    if (n > 1) {
      failures.push({ assertion: 'no schematic path appears more than once', detail: `path ${p} appears ${n} times` });
    }
  }

  // 5. Board-level blocks are Board A's, except the ones the plan took.
  const taken = takenBlockNames(plan);
  for (const block of diff.global.blocks) {
    if (block.status === 'identical' || block.status === 'absent') continue;
    if (taken.has(block.block)) continue;
    failures.push({
      assertion: `the \`${block.block}\` block is carried through from Board A untouched`,
      detail: 'it differs from Board A’s, and the plan did not take it',
    });
  }
  // Where nets are named inline (KiCad 10) there is no table to carry through:
  // the set of net names is a consequence of which objects are in the file, so
  // taking objects necessarily changes it. Asserting otherwise would refuse
  // every merge on such a board. What matters — that nothing changed outside
  // the selections — is asserted above and covers it.
  if (!a.usesNamedNets && !diff.global.nets.identicalNames && !plan.takeGlobal.has('nets')) {
    failures.push({
      assertion: "Board A's net names are carried through untouched",
      detail:
        `${diff.global.nets.onlyInA.length} net(s) lost, ${diff.global.nets.onlyInB.length} gained, ` +
        `and the plan did not take the net table`,
    });
  }

  return { ok: failures.length === 0, failures, counts: actual, preExistingDuplicateUuids: carriedOver };
}

/** File-level block names corresponding to the plan's global selections. */
function takenBlockNames(plan: MergePlan): Set<string> {
  const out = new Set<string>();
  for (const id of plan.takeGlobal) {
    if (id !== 'nets') out.add(id);
  }
  return out;
}

function countByType(board: Board): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of board.objects) out[o.type] = (out[o.type] ?? 0) + 1;
  return out;
}

export function collectUuids(root: Node): string[] {
  const out: string[] = [];
  for (const n of walk(root)) {
    const u = getUuid(n);
    if (u !== undefined && u !== '') out.push(u);
  }
  return out;
}

export function findDuplicateUuids(root: Node): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const u of collectUuids(root)) {
    if (seen.has(u)) dupes.add(u);
    else seen.add(u);
  }
  return [...dupes];
}
